import { memo, useState, useRef, useEffect, useCallback } from "react";
import { Search, X, Sun, Wind, Anchor, Target, Users, Phone, HelpCircle, Zap, Building2 } from "lucide-react";

const LOGO_URL = "/george_energy_logo_optimized.png";

// Search suggestions with categories - aligned with George Energy's approach
const SEARCH_SUGGESTIONS = [
  // Sectors
  { id: "solar", label: "Solar Energy", description: "Utility-scale solar development", icon: Sun, category: "Sectors", page: "solar" },
  { id: "wind", label: "Wind Energy", description: "Wind energy development", icon: Wind, category: "Sectors", page: "wind" },
  { id: "public-infra", label: "Public Infrastructure", description: "Civic energy infrastructure", icon: Building2, category: "Sectors", page: "public-infra" },
  { id: "marine", label: "Marine Energy", description: "Research-stage focus area", icon: Anchor, category: "Sectors", page: "marine" },
  
  // Company
  { id: "about", label: "About George Energy", description: "Our philosophy and approach", icon: Users, category: "Company", page: "about" },
  { id: "approach", label: "Our Approach", description: "Development philosophy", icon: Target, category: "Company", section: "approach" },
  { id: "pipeline", label: "Progress & Pipeline", description: "Current development activities", icon: Zap, category: "Company", section: "pipeline" },
  
  // Contact
  { id: "contact", label: "Contact Us", description: "Start a conversation", icon: Phone, category: "Contact", page: "contact" },
  
  // Help
  { id: "faq", label: "Learn About Energy", description: "Understand energy systems", icon: HelpCircle, category: "Quick Actions", action: "educate" },
];

export const Header = memo(({ currentPage, setCurrentPage, onOpenChat, isOverlay = false }) => {
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredSuggestions, setFilteredSuggestions] = useState([]);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const searchInputRef = useRef(null);
  const searchContainerRef = useRef(null);

  // Listen for dark mode changes
  useEffect(() => {
    const checkDarkMode = () => {
      setIsDarkMode(document.documentElement.classList.contains('dark-mode'));
    };
    
    // Initial check
    checkDarkMode();
    
    // Watch for class changes on html element
    const observer = new MutationObserver(checkDarkMode);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    
    return () => observer.disconnect();
  }, []);

  const goToHome = () => {
    window.dispatchEvent(new CustomEvent('resetHomeScroll'));
    setCurrentPage('home');
  };

  const goToAbout = () => {
    window.dispatchEvent(new CustomEvent('resetAboutScroll'));
    setCurrentPage('about');
  };

  const goToContact = () => {
    setCurrentPage('contact');
  };

  // Filter suggestions based on search query
  useEffect(() => {
    if (searchQuery.trim() === "") {
      // Show popular suggestions when empty
      setFilteredSuggestions(SEARCH_SUGGESTIONS.slice(0, 6));
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = SEARCH_SUGGESTIONS.filter(
        item =>
          item.label.toLowerCase().includes(query) ||
          item.description.toLowerCase().includes(query) ||
          item.category.toLowerCase().includes(query)
      );
      setFilteredSuggestions(filtered);
    }
  }, [searchQuery]);

  // Focus input when search opens
  useEffect(() => {
    if (isSearchOpen && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 100);
    }
  }, [isSearchOpen]);

  // Close search on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
        setIsSearchOpen(false);
        setSearchQuery("");
      }
    };

    if (isSearchOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isSearchOpen]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        setIsSearchOpen(false);
        setSearchQuery("");
      }
      // Cmd/Ctrl + K to open search
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setIsSearchOpen(true);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Handle suggestion click
  const handleSuggestionClick = useCallback((suggestion) => {
    setIsSearchOpen(false);
    setSearchQuery("");

    if (suggestion.page) {
      // Navigate to page
      if (suggestion.page === "about") {
        goToAbout();
      } else if (suggestion.page === "contact") {
        goToContact();
      } else {
        // Navigate to service pages (solar, wind, marine)
        window.dispatchEvent(new CustomEvent('navigate', { detail: suggestion.page }));
      }
    } else if (suggestion.section) {
      // Scroll to section on home page
      setCurrentPage('home');
      setTimeout(() => {
        const element = document.getElementById(suggestion.section);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else if (suggestion.action === "educate") {
      // Open chat for education
      if (onOpenChat) {
        onOpenChat();
      }
    }
  }, [setCurrentPage, onOpenChat]);

  // Group suggestions by category
  const groupedSuggestions = filteredSuggestions.reduce((acc, item) => {
    if (!acc[item.category]) {
      acc[item.category] = [];
    }
    acc[item.category].push(item);
    return acc;
  }, {});

  return (
    <div ref={searchContainerRef} className="relative">
      <header role="banner">
        <nav 
          className={`${isOverlay ? 'fixed' : 'sticky'} top-0 left-0 right-0 z-40 bg-white/95 dark:bg-slate-900/95 backdrop-blur-md shadow-sm border-b border-gray-400/40 dark:border-slate-700/50`}
          role="navigation"
          aria-label="Main navigation"
        >
        <div className="container mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-14 sm:h-16">
            {/* Logo */}
            <a 
              href="#"
              className="cursor-pointer flex items-center justify-center h-full" 
              onClick={(e) => { e.preventDefault(); goToHome(); }}
              data-testid="header-logo"
              aria-label="George Energy - Go to homepage"
            >
              <img 
                src={LOGO_URL} 
                alt="George Energy logo" 
                className="h-16 sm:h-20 lg:h-[88px] w-auto max-w-[240px] sm:max-w-[300px] lg:max-w-[360px] object-contain" 
                loading="eager"
              />
            </a>

            {/* Navigation + Search */}
            <div className="flex items-center gap-2 sm:gap-6">
              {/* Nav Links - visible on all screen sizes */}
              <div className="flex items-center gap-2 sm:gap-6" role="menubar">
                <button 
                  onClick={goToHome}
                  className={`min-h-[24px] py-1 text-xs sm:text-sm font-medium transition-colors ${currentPage === 'home' ? 'text-lime-600 dark:text-lime-400' : 'text-emerald-800 dark:text-white hover:text-lime-600 dark:hover:text-lime-400'}`}
                  role="menuitem"
                  aria-current={currentPage === 'home' ? 'page' : undefined}
                  data-testid="nav-home"
                >
                  Home
                </button>
                <button 
                  onClick={goToAbout}
                  className={`min-h-[24px] py-1 text-xs sm:text-sm font-medium transition-colors ${currentPage === 'about' ? 'text-lime-600 dark:text-lime-400' : 'text-emerald-800 dark:text-white hover:text-lime-600 dark:hover:text-lime-400'}`}
                  data-testid="nav-about"
                  role="menuitem"
                  aria-current={currentPage === 'about' ? 'page' : undefined}
                >
                  About
                </button>
                <button 
                  onClick={goToContact}
                  className={`min-h-[24px] py-1 text-xs sm:text-sm font-medium transition-colors ${currentPage === 'contact' ? 'text-lime-600 dark:text-lime-400' : 'text-emerald-800 dark:text-white hover:text-lime-600 dark:hover:text-lime-400'}`}
                  data-testid="nav-contact"
                  role="menuitem"
                  aria-current={currentPage === 'contact' ? 'page' : undefined}
                >
                  Contact
                </button>
              </div>

              {/* Search Icon - Matching accessibility and chat icon style */}
              <button
                onClick={() => setIsSearchOpen(!isSearchOpen)}
                className="w-10 h-10 rounded-full bg-emerald-600 text-white flex items-center justify-center shadow-lg hover:bg-emerald-700 transition-all duration-200"
                data-testid="search-toggle"
                aria-label="Search"
              >
                {isSearchOpen ? (
                  <X className="w-5 h-5" />
                ) : (
                  <Search className="w-5 h-5" />
                )}
              </button>
            </div>
          </div>
        </div>
      </nav>
      </header>

      {/* Search Dropdown */}
      <div
        className={`fixed left-0 right-0 z-30 transition-all duration-300 ease-out ${
          isSearchOpen
            ? 'opacity-100 translate-y-0'
            : 'opacity-0 -translate-y-4 pointer-events-none'
        }`}
        style={{ top: isOverlay ? '56px' : '56px' }}
      >
        {/* Backdrop */}
        <div 
          className={`fixed inset-0 bg-black/20 backdrop-blur-sm transition-opacity duration-300 ${
            isSearchOpen ? 'opacity-100' : 'opacity-0'
          }`}
          style={{ top: isOverlay ? '56px' : '56px' }}
        />

        {/* Search Container */}
        <div className="relative container mx-auto px-4 sm:px-6 pt-4">
          <div 
            className={`search-panel-container rounded-2xl shadow-2xl border overflow-hidden max-w-2xl mx-auto ${
              isDarkMode 
                ? 'bg-slate-800 border-slate-600' 
                : 'bg-white border-gray-400/40'
            }`}
            data-testid="search-panel"
          >
            {/* Search Input */}
            <div className={`flex items-center gap-3 px-4 py-3 border-b ${isDarkMode ? 'border-slate-600' : 'border-gray-400/40'}`}>
              <Search className={`w-5 h-5 flex-shrink-0 ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`} aria-hidden="true" />
              <label htmlFor="header-search-input" className="sr-only">Search website</label>
              <input
                id="header-search-input"
                ref={searchInputRef}
                type="text"
                placeholder="Search sectors, pages..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={`flex-1 bg-transparent outline-none text-base ${
                  isDarkMode 
                    ? 'text-white placeholder:text-slate-400' 
                    : 'text-emerald-900 placeholder:text-emerald-600'
                }`}
                data-testid="search-input"
                aria-describedby="search-hint"
                autoComplete="off"
              />
              <kbd className={`hidden sm:inline-flex items-center gap-1 px-2 py-1 text-xs rounded border ${
                isDarkMode 
                  ? 'text-slate-300 bg-slate-700 border-slate-500' 
                  : 'text-emerald-700 bg-emerald-50 border-gray-400/50'
              }`} aria-hidden="true">
                <span className="text-xs">⌘</span>K
              </kbd>
            </div>

            {/* Suggestions */}
            <div className="max-h-[60vh] overflow-y-auto py-2">
              {filteredSuggestions.length === 0 ? (
                <div className="px-4 py-8 text-center">
                  <p className={`text-sm ${isDarkMode ? 'text-slate-300' : 'text-emerald-700'}`}>No results found for "{searchQuery}"</p>
                  <p className={`text-xs mt-1 ${isDarkMode ? 'text-slate-400' : 'text-emerald-600'}`}>Try searching for solar, wind, approach, or contact</p>
                </div>
              ) : (
                Object.entries(groupedSuggestions).map(([category, items]) => (
                  <div key={category} className="mb-2">
                    <p className={`px-4 py-2 text-xs font-semibold uppercase tracking-wider ${isDarkMode ? 'text-slate-400' : 'text-emerald-700'}`}>
                      {category}
                    </p>
                    {items.map((suggestion) => {
                      const IconComponent = suggestion.icon;
                      return (
                        <button
                          key={suggestion.id}
                          onClick={() => handleSuggestionClick(suggestion)}
                          className={`w-full flex items-center gap-3 px-4 py-3 transition-colors text-left group ${
                            isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-emerald-50'
                          }`}
                          data-testid={`search-suggestion-${suggestion.id}`}
                        >
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors ${
                            isDarkMode 
                              ? 'bg-slate-700 border border-slate-500 group-hover:bg-slate-600' 
                              : 'bg-gradient-to-br from-emerald-100 to-lime-100 group-hover:from-emerald-200 group-hover:to-lime-200'
                          }`}>
                            <IconComponent className={`w-5 h-5 ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm font-medium ${isDarkMode ? 'text-white group-hover:text-emerald-300' : 'text-emerald-900 group-hover:text-emerald-700'}`}>
                              {suggestion.label}
                            </p>
                            <p className={`text-xs truncate ${isDarkMode ? 'text-slate-400' : 'text-emerald-700'}`}>
                              {suggestion.description}
                            </p>
                          </div>
                          <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                            <span className={`text-xs ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>→</span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                ))
              )}
            </div>

            {/* Footer hint */}
            <div className={`px-4 py-2 border-t ${isDarkMode ? 'bg-slate-900 border-slate-600' : 'bg-emerald-50/50 border-gray-400/40'}`}>
              <p className={`text-xs text-center ${isDarkMode ? 'text-slate-400' : 'text-emerald-700'}`}>
                Press <kbd className={`px-1.5 py-0.5 rounded border ${isDarkMode ? 'bg-slate-700 border-slate-500 text-slate-300' : 'bg-white border-gray-400/50 text-emerald-600'}`}>ESC</kbd> to close
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});
